import { zonedDateTimeToIso, localTimeZone } from '@/lib/timezone'

export const PRIORITIES = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' },
  { id: 'critical', label: 'Critical' },
] as const
export const METHODOLOGIES = [
  { id: 'kanban', label: 'Kanban', hint: 'Continuous flow, no fixed iterations.' },
  { id: 'scrum', label: 'Scrum', hint: 'Fixed-length iterations with a review at the end of each.' },
  { id: 'waterfall', label: 'Waterfall', hint: 'Phases in order, each signed off before the next.' },
  { id: 'hybrid', label: 'Hybrid', hint: 'A planned backbone with flexible delivery inside it.' },
] as const
export const HEALTHS = [
  { id: 'on_track', label: 'On track', tone: 'ok' },
  { id: 'at_risk', label: 'At risk', tone: 'warn' },
  { id: 'off_track', label: 'Off track', tone: 'danger' },
] as const
export const SEVERITIES = ['low', 'medium', 'high'] as const
export const RISK_STATUSES = ['open', 'mitigated', 'closed', 'occurred'] as const

export type Priority = (typeof PRIORITIES)[number]['id']
export type Methodology = (typeof METHODOLOGIES)[number]['id']
export type Health = (typeof HEALTHS)[number]['id']

export interface Metric { id: string; name: string; unit: string; baseline: number | null; target: number | null; current: number | null; dueAt?: string | null }
export interface Milestone { id: string; name: string; dueAt: string | null; doneAt: string | null; phase?: string | null }
export type Level3 = 'low' | 'medium' | 'high'
export type OwnerTeam = 'am' | 'ops' | 'pe' | 'client'
export interface Risk { id: string; text: string; severity: (typeof SEVERITIES)[number]; mitigation: string; status: (typeof RISK_STATUSES)[number]; likelihood?: Level3 | null; impact?: Level3 | null; ownerTeam?: OwnerTeam | null }
export interface Stakeholder { memberId: string | null; name: string; role: string }

export interface PlanView {
  projectId: string
  aim: string | null
  strategy: string | null
  scopeIn: string | null
  scopeOut: string | null
  expectedOutcomes: string | null
  assumptions: string | null
  dependencies: string | null
  priority: Priority | null
  methodology: Methodology | null
  health: Health | null
  healthNote: string | null
  budget: string | null
  metrics: Metric[]
  milestones: Milestone[]
  risks: Risk[]
  stakeholders: Stakeholder[]
  version: number | null
}

export interface PlanResponse {
  plan: PlanView
  timeline: { startAt: string | null; targetEndAt: string | null }
  progress: { total: number; done: number; percent: number; overdue: number; byStatus: Record<string, number> }
  metricProgress: Record<string, number | null>
}

/** The form's state: strings for everything a person types, so a half-typed number is not lost. */
export interface PlanDraft {
  aim: string
  strategy: string
  scopeIn: string
  scopeOut: string
  expectedOutcomes: string
  assumptions: string
  dependencies: string
  priority: Priority | ''
  methodology: Methodology | ''
  health: Health | ''
  healthNote: string
  budget: string
  startDate: string
  endDate: string
  metrics: Array<{ id: string; name: string; unit: string; baseline: string; target: string; current: string; due?: string }>
  milestones: Array<{ id: string; name: string; date: string; done: boolean; phase?: string | null }>
  risks: Risk[]
  stakeholders: Stakeholder[]
}

let counter = 0
export const newRowId = () => `${Date.now().toString(36)}${(counter++).toString(36)}${Math.random().toString(36).slice(2, 6)}`

export const emptyDraft = (): PlanDraft => ({
  aim: '', strategy: '', scopeIn: '', scopeOut: '', expectedOutcomes: '', assumptions: '', dependencies: '',
  priority: '', methodology: '', health: '', healthNote: '', budget: '', startDate: '', endDate: '',
  metrics: [], milestones: [], risks: [], stakeholders: [],
})

const dayOf = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-CA') : '')
const numText = (n: number | null) => (n === null ? '' : String(n))

export function draftFromPlan(res: PlanResponse): PlanDraft {
  const p = res.plan
  return {
    aim: p.aim ?? '', strategy: p.strategy ?? '', scopeIn: p.scopeIn ?? '', scopeOut: p.scopeOut ?? '',
    expectedOutcomes: p.expectedOutcomes ?? '', assumptions: p.assumptions ?? '', dependencies: p.dependencies ?? '',
    priority: p.priority ?? '', methodology: p.methodology ?? '', health: p.health ?? '', healthNote: p.healthNote ?? '', budget: p.budget ?? '',
    startDate: dayOf(res.timeline.startAt), endDate: dayOf(res.timeline.targetEndAt),
    metrics: p.metrics.map((m) => ({ id: m.id, name: m.name, unit: m.unit, baseline: numText(m.baseline), target: numText(m.target), current: numText(m.current), due: dayOf(m.dueAt ?? null) })),
    milestones: p.milestones.map((m) => ({ id: m.id, name: m.name, date: dayOf(m.dueAt), done: Boolean(m.doneAt), phase: m.phase ?? null })),
    risks: p.risks, stakeholders: p.stakeholders,
  }
}

const toNum = (s: string): number | null => {
  const n = Number(s.trim())
  return s.trim() === '' || !Number.isFinite(n) ? null : n
}
const dateIso = (date: string) => (date ? zonedDateTimeToIso(date, '17:00', localTimeZone()) : null)

/** What PUT /project-plans/:id takes. Rows with no name are dropped, not saved blank. */
export function draftToInput(d: PlanDraft) {
  return {
    aim: d.aim.trim() || null, strategy: d.strategy.trim() || null, scopeIn: d.scopeIn.trim() || null, scopeOut: d.scopeOut.trim() || null,
    expectedOutcomes: d.expectedOutcomes.trim() || null, assumptions: d.assumptions.trim() || null, dependencies: d.dependencies.trim() || null,
    priority: d.priority || null, methodology: d.methodology || null, health: d.health || null, healthNote: d.healthNote.trim() || null, budget: d.budget.trim() || null,
    metrics: d.metrics.filter((m) => m.name.trim()).map((m) => ({ id: m.id, name: m.name.trim(), unit: m.unit.trim(), baseline: toNum(m.baseline), target: toNum(m.target), current: toNum(m.current), dueAt: m.due ? dateIso(m.due) : null })),
    milestones: d.milestones.filter((m) => m.name.trim()).map((m) => ({ id: m.id, name: m.name.trim(), dueAt: dateIso(m.date), doneAt: m.done ? new Date().toISOString() : null, phase: m.phase ?? null })),
    risks: d.risks.filter((r) => r.text.trim()),
    stakeholders: d.stakeholders.filter((s) => s.name.trim()),
  }
}

export const timelineBody = (d: PlanDraft) => ({ tentativeStartAt: dateIso(d.startDate), tentativeCompletionAt: d.endDate ? zonedDateTimeToIso(d.endDate, '23:00', localTimeZone()) : null })

export const isPlanEmpty = (d: PlanDraft): boolean => {
  const e = emptyDraft()
  return (Object.keys(e) as Array<keyof PlanDraft>).every((k) => JSON.stringify(d[k]) === JSON.stringify(e[k]))
}

/** How much of a good plan is filled in, 0–100: the nudge on the project page. */
export function planCompleteness(d: PlanDraft): { percent: number; missing: string[] } {
  const checks: Array<[string, boolean]> = [
    ['an aim', Boolean(d.aim.trim())],
    ['a strategy', Boolean(d.strategy.trim())],
    ['success metrics', d.metrics.some((m) => m.name.trim() && m.target.trim())],
    ['expected outcomes', Boolean(d.expectedOutcomes.trim())],
    ['a start and target end date', Boolean(d.startDate && d.endDate)],
    ['milestones', d.milestones.some((m) => m.name.trim())],
    ['a priority', Boolean(d.priority)],
    ['risks', d.risks.some((r) => r.text.trim())],
  ]
  return { percent: Math.round((checks.filter(([, ok]) => ok).length / checks.length) * 100), missing: checks.filter(([, ok]) => !ok).map(([label]) => label) }
}

/** "120 → 20 tickets (−83%)": what the project is expected to change. */
export function improvementText(m: Pick<Metric, 'baseline' | 'target' | 'unit'>): string | null {
  if (m.baseline === null || m.target === null) return null
  const unit = m.unit ? ` ${m.unit}` : ''
  if (m.baseline === 0) return `${m.baseline} → ${m.target}${unit}`
  const pct = Math.round(((m.target - m.baseline) / Math.abs(m.baseline)) * 100)
  return `${m.baseline} → ${m.target}${unit} (${pct > 0 ? '+' : pct < 0 ? '−' : ''}${Math.abs(pct)}%)`
}

export type RequiredKey = 'aim' | 'metrics' | 'dates' | 'priority' | 'milestones'

/**
 * What a plan must say before a project is opened. Everything else is encouraged, these are not:
 * why, how we will know, when, how urgent, and the dates on the way.
 */
export function requiredProblems(d: PlanDraft): Array<{ key: RequiredKey; step: 'aim' | 'timeline'; label: string }> {
  const out: Array<{ key: RequiredKey; step: 'aim' | 'timeline'; label: string }> = []
  if (!d.aim.trim()) out.push({ key: 'aim', step: 'aim', label: 'an aim' })
  if (!d.metrics.some((m) => m.name.trim() && m.target.trim() !== '' && Number.isFinite(Number(m.target)))) out.push({ key: 'metrics', step: 'aim', label: 'a success metric with a target' })
  if (!d.startDate || !d.endDate || d.endDate < d.startDate) out.push({ key: 'dates', step: 'timeline', label: 'a start and target end date' })
  if (!d.priority) out.push({ key: 'priority', step: 'timeline', label: 'a priority' })
  if (!d.milestones.some((m) => m.name.trim() && m.date)) out.push({ key: 'milestones', step: 'timeline', label: 'a dated milestone' })
  return out
}
